/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { TextValueTranslation } from '@msdyn365-commerce/retail-proxy';

import { isMobile } from '../../components/breakpoints';
import { buildLocalizedLanguageUrls } from '../../components/localized-language-url';

import { QuickLink } from './components/ecomm-quick-link';

import { IEcommSiteMenuData } from './ecomm-site-menu.data';
import { IEcommSiteMenuProps } from './ecomm-site-menu.props.autogenerated';

export interface IEcommSiteMenuViewProps extends IEcommSiteMenuProps<IEcommSiteMenuData> {
    qLinks: React.ReactNode[];
    quicklinksTitle: string;
    isProduction: boolean;
    homeUrl: string;
    cartUrl: string;
    cartTitle: string;
    signInTitle: string;
    languageTitles: string[];
    languageUrl: string[];
}

/**
 *
 * EcommSiteMenu component
 * @extends {React.PureComponent<IEcommSiteMenuProps<IEcommSiteMenuData>>}
 */
class EcommSiteMenu extends React.PureComponent<IEcommSiteMenuProps<IEcommSiteMenuData>> {
    public state: any = { width: 0 };

    public componentDidMount(): void {
        if (!this.state.hasMounted) {
            window.addEventListener('resize', this._updateDimensions);

            this.setState({ width: window.innerWidth });
        }
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this._updateDimensions);
    }

    public render(): JSX.Element | null {
        const { config } = this.props;

        const LanguageTitles: string[] = [];
        const LanguageUrl: string[] = [];

        if (config.languages && config.languages.length > 0) {
            const languages = config.languages;
            const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

            for (let i = 0; i < languages.length; ++i) {
                LanguageTitles.push(languages[`${i}`].languageName);
            }

            LanguageUrl.push(...buildLocalizedLanguageUrls(languages, currentUrl));
        }

        const EcommSiteMenuViewProps = {
            ...this.props,
            qLinks: this._generateQuickLinks(),
            quicklinksTitle: config.quickLinksTitle,
            isProduction: config.isProduction,
            homeUrl: config.homeUrl.destinationUrl,
            cartUrl: config.cartUrl.destinationUrl,
            cartTitle: config.cartTitle,
            signInTitle: config.signInTitle,
            languageTitles: LanguageTitles,
            languageUrl: LanguageUrl
        };

        return this.props.renderView(EcommSiteMenuViewProps);
    }

    private _generateQuickLinks(): JSX.Element[] | null {
        const {
            config,
            context,
            data: { categories }
        } = this.props;

        const cart = this.props.data.cart.result;

        const qLinks: JSX.Element[] = [];
        const locale = context.actionContext.requestContext.locale;
        const localeCode = context.actionContext.requestContext.locale.split('-')[0];

        if (categories && categories.result) {
            const category = categories.result;

            if (config.quicklinks) {
                for (let i = 0; i < config.quicklinks.length; i++) {
                    const categoryUrl = config.quicklinks[`${i}`].categoryLink
                        ? config.quicklinks[`${i}`].categoryLink?.destinationUrl
                        : '';
                    const quicklinkName = config.quicklinks[`${i}`].quicklinkName;
                    const categoryIndex = category.findIndex(item => item.Name?.toLowerCase() === quicklinkName.toLowerCase());
                    const filter = config.quicklinks[`${i}`].filterName;
                    const enbaleNewLable = config.quicklinks[`${i}`].enableNewLabel;
                    const newLabelText = config.quicklinks[`${i}`].enableNewLabel ? config.quicklinks[`${i}`].newLableText : '';

                    if (categoryIndex > -1) {
                        const categoryId = category[`${categoryIndex}`].RecordId;
                        const categoryLocale = category[`${categoryIndex}`].NameTranslations?.find(
                            (item: TextValueTranslation) => item.Language === locale
                        );
                        const categoryLocaleName = categoryLocale ? (categoryLocale.Text ? categoryLocale.Text : '') : quicklinkName;

                        let currentCategory = category[`${categoryIndex}`];

                        while (currentCategory.ParentCategory) {
                            const parentCategory = category.find(item => item.RecordId === currentCategory.ParentCategory);

                            if (parentCategory) {
                                currentCategory = parentCategory;
                            } else {
                                break;
                            }
                        }

                        const url = categoryUrl ? categoryUrl : '';
                        const target = config.quicklinks[`${i}`].isPortal ? '_blank' : '_self';

                        qLinks.push(
                            <QuickLink
                                name={categoryLocaleName}
                                categoryId={categoryId}
                                url={url}
                                target={target}
                                filterName={filter}
                                isPortal={config.quicklinks[`${i}`].isPortal}
                                mobileMode={isMobile(this.state.width)}
                                locale={localeCode}
                                context={context}
                                cart={cart}
                                enableNewLable={enbaleNewLable}
                                newLableText={newLabelText}
                            />
                        );
                    } else if (config.quicklinks[`${i}`].categoryLink?.destinationUrl) {
                        const filter = config.quicklinks[`${i}`].filterName;
                        const categoryLocaleName = quicklinkName;
                        const categoryUrl = config.quicklinks[`${i}`].categoryLink?.destinationUrl;
                        const url = categoryUrl ? categoryUrl : '';
                        const target = config.quicklinks[`${i}`].isPortal ? '_blank' : '_self';

                        qLinks.push(
                            <QuickLink
                                name={categoryLocaleName}
                                categoryId={0}
                                url={url}
                                target={target}
                                filterName={filter}
                                isPortal={config.quicklinks[`${i}`].isPortal}
                                mobileMode={isMobile(this.state.width)}
                                locale={localeCode}
                                context={context}
                                cart={cart}
                                enableNewLable={enbaleNewLable}
                                newLableText={newLabelText}
                            />
                        );
                    }
                }
            }
        }

        return qLinks;
    }

    private _updateDimensions = () => {
        this.setState({ width: window.innerWidth });
    };
}

export default EcommSiteMenu;
